import React from "react"
import { lighten } from "polished"
import { Link } from "gatsby"
import { LazyLoadImage } from "react-lazy-load-image-component"
import { Fade } from "react-reveal"
import Swiper from "react-id-swiper"
import "swiper/css/swiper.css"

import Layout from "../components/layout"
import SEO from "../components/seo"
import styled from "styled-components"
import blob from "../assets/images/blob-shape.svg"
import blobBlue from "../assets/images/blob-shape-blue.svg"
import iphoneMock from "../assets/images/iphone-mock.png"
import onlineDeposits from "../assets/images/online-deposits.jpg"
import messengerOpen from "../assets/images/messenger-open.jpg"
import chatMobile from "../assets/images/live-chat-mobile.jpg"
import exitIntent from "../assets/images/exit-intent.jpg"
import exitIntentMobile from "../assets/images/exit-intent-mobile.jpg"
import socialProof from "../assets/images/social-proof.jpg"
import socialProofMobile from "../assets/images/social-proof-mobile.jpg"
import googleReviews from "../assets/images/google-reviews.png"
import drivenPitch from "../assets/images/driven-pitch.jpg"
import ajCarsLogo from "../assets/images/aj-cars-logo.png"
import PCI from "../assets/images/pci.svg"
import BlogPost from "../components/BlogPost"
import ContactBanner from "../components/ContactBanner"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
  faAngleRight,
  faWrench,
  faFilter,
  faGlobe,
} from "@fortawesome/free-solid-svg-icons"
import Feature from "../components/Feature"

const StyledHero = styled.section`
  position: relative;
  padding: 6rem 0;
  background-color: ${({ theme }) => theme.colors.obsidian};
  color: ${({ theme }) => theme.colors.grey1};
  text-align: center;
  h1 {
    color: #fff;
  }
  h2 {
    margin-bottom: 2rem;
  }
  .hero-grid {
    z-index: 1;
    position: relative;
    display: grid;
    grid-template-columns: 1fr;
    grid-gap: 4rem;
    .presentation {
      margin: 0 auto;
      max-width: 275px;
      overflow: hidden;
      position: relative;
      .placeholder {
        position: relative;
        max-width: 100%;
        height: 600px;
        z-index: 1;
        display: block;
      }
      .swiper-container {
        background-color: #000;
        position: absolute;
        top: 0;
        left: 0;
        z-index: 0;
        height: 100%;
        margin: 18px;
        padding-top: 21px;
        box-sizing: border-box;
        max-width: 239px;
        max-height: 564px;
      }
    }
  }
  .cta-container {
    display: grid;
    grid-gap: 2rem;
    align-items: center;
    margin-bottom: 2rem;
    .reviews {
      max-width: 125px;
      margin: 0 auto;
    }
  }
  .brands {
    .logos {
      display: grid;
      grid-template-columns: 1fr 1fr 1fr;
      align-items: center;
      img {
        max-height: 45px;
        max-width: 85px;
        margin: 0 auto;
      }
    }
  }
  .blob {
    position: absolute;
    right: -235px;
    top: 0;
    transform: scale(1.5);
  }
  .bg-transition {
    position: absolute;
    left: 0%;
    bottom: 0;
    width: 100%;
    height: 220px;
    background: linear-gradient(
      174deg,
      rgba(255, 255, 255, 0) 0%,
      rgba(155, 155, 155, 0) 41%,
      rgba(255, 255, 255, 1) 41%,
      rgba(255, 255, 255, 1) 100%
    );
  }
  @media only screen and (min-width: 1000px) {
    text-align: left;
    .cta-container {
      margin-bottom: 2rem;
      grid-template-columns: 1fr 1fr;
    }
    .hero-grid {
      grid-template-columns: 1fr 1fr;
    }
    .brands {
      .logos {
        display: flex;
        img {
          max-height: 60px;
          max-width: 120px;
          margin-right: 4rem;
        }
      }
    }
  }
  @media only screen and (min-width: 1200px) {
    .blob {
      position: absolute;
      right: -200px;
      top: 0;
      transform: scale(2.5);
    }
  }
`

const StyledFeatures = styled.section`
  z-index: 1;
  position: relative;
  overflow: hidden;
  width: 100%;
  background-color: #fff;
  .blob {
    position: absolute;
    transform: scale(2.5);
    z-index: 0;
    fill: #366df8;
  }
  .blob-1 {
    left: -600px;
    top: 0;
    bottom: 0;
    opacity: 0.05;
    margin: auto;
  }
  .blob-2 {
    right: -600px;
    bottom: 300px;
    opacity: 0.15;
  }
  .heading {
    margin-top: 0;
  }
  .container {
    position: relative;
    z-index: 1;
  }
`

const StyledTestimonial = styled.section`
  background: linear-gradient(
    174deg,
    rgba(255, 255, 255, 0) 0%,
    rgba(155, 155, 155, 0) 41%,
    ${({ theme }) => theme.colors.obsidian} 41%,
    ${({ theme }) => theme.colors.obsidian} 100%
  );
  padding-bottom: 4rem;
  .featured-image {
    background: url(${drivenPitch});
    background-size: cover;
    background-position: center;
    padding: 4rem 1rem;
    color: #fff;
    box-shadow: 0 70px 130px -20px rgba(0, 39, 90, 0.25);
    h4 {
      margin-bottom: 2rem;
      font-size: 1.5rem;
    }
    p {
      margin: 0;
      color: ${({ theme }) => theme.colors.grey1};
      strong {
        color: #fff;
      }
    }
    .logo {
      margin-bottom: 2rem;
    }
  }
  @media only screen and (min-width: 900px) {
    .featured-image {
      padding: 5rem;
      h4 {
        font-size: 2rem;
      }
    }
  }
  @media only screen and (min-width: 900px) {
    .featured-image {
      padding: 10rem;
    }
  }
`

const StyledBlog = styled.section`
  background-color: ${({ theme }) => theme.colors.obsidian};
  color: #fff;
  .heading {
    margin-top: 0;
  }
  .carousel-wrapper {
    overflow-y: scroll;
    -webkit-overflow-scrolling: touch;
    padding-bottom: 4rem;
  }
  .carousel {
    display: grid;
    grid-template-columns: 1fr 1fr 1fr;
    max-width: 1200px;
    margin: 0 auto;
    .post {
      min-width: 300px;
      padding: 0 1rem;
      &:first-child {
        padding-left: 2rem;
      }
      &:last-child {
        padding-right: 2rem;
      }
      .meta {
        background-color: ${({ theme }) => lighten(0.1, theme.colors.obsidian)};
        h5 {
          color: #fff;
        }
        .description {
          color: ${({ theme }) => theme.colors.grey1};
        }
      }
    }
  }
`

export const pageQuery = graphql`
  query IndexQuery {
    allContentfulBlogPost(
      limit: 3
      filter: { tags: { in: "dealer9000" } }
      sort: { fields: publishDate, order: DESC }
    ) {
      edges {
        node {
          title
          description {
            description
          }
          tags
          slug
          heroImage {
            title
            resize(width: 500) {
              src
              width
              height
            }
          }
        }
      }
    }
  }
`

const IndexPage = ({ data }) => (
  <Layout>
    <SEO title="We Make Your Dealer Website Generate More Sales" />
    <StyledHero>
      <img className="blob" src={blob} alt="Blob" />
      <Fade>
        <div className="container hero-grid">
          <div>
            <h1>
              We make your dealer website generate more sales.{" "}
              <span>Instantly.</span>
            </h1>
            <h2 className="h1-sub light">
              Start making <strong>online sales</strong> and{" "}
              <strong>increase leads</strong> for just £29/month
            </h2>
            <div className="cta-container">
              <div>
                <Link to="/get-started" className="btn">
                  Start free trial
                </Link>
              </div>
              <LazyLoadImage
                className="reviews"
                src={googleReviews}
                alt="Google Reviews"
              />
            </div>
            <div className="brands">
              <p>11 dealers have joined this week</p>
              <div className="logos">
                <a
                  href="https://www.aj-cars.co.uk"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <LazyLoadImage src={ajCarsLogo} alt="AJ Cars" />
                </a>
                <a
                  href="https://www.drivengroup.co.uk"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <LazyLoadImage
                    src="https://automerce.imgix.net/9/assets/c4ebe4?w=200"
                    alt="Driven Group"
                  />
                </a>
                <LazyLoadImage
                  src="https://automerce.imgix.net/10/assets/2abf2a?w=200"
                  alt="Prince Motor Group"
                />
              </div>
            </div>
          </div>
          <div className="presentation">
            <img className="placeholder" src={iphoneMock} alt="iPhone" />
            <Swiper width={239} autoplay={true}>
              <img src={chatMobile} alt="Dealer 9000 Chat Mobile" />
              <img src={exitIntentMobile} alt="Dealer 9000 Exit Intent" />
              <img src={socialProofMobile} alt="Dealer 9000 Social Proof" />
            </Swiper>
          </div>
        </div>
      </Fade>
      <div className="bg-transition"></div>
    </StyledHero>
    <StyledFeatures id="features">
      <img className="blob blob-1" src={blobBlue} alt="Blob" />
      <img className="blob blob-2" src={blobBlue} alt="Blob" />
      <div className="container">
        <div className="heading text-center">
          <h2>Dealer 9000 Supercharges Your Website</h2>
          <p>
            Dealer 9000 can easily be installed onto your website, start
            increasing sales today
          </p>
        </div>
        <Feature>
          <div className="text-image">
            <LazyLoadImage
              src={onlineDeposits}
              alt="Online Deposits"
              className="media"
            />
            <div className="content">
              <h3 className="title">Online Deposits</h3>
              <h4>Start Dealing 24/7</h4>
              <p>
                The average dealer website has hundreds if not thousands of
                visitors in a single day. Many of these visitors will be
                browsing out of business hours.
              </p>
              <p>
                Online Deposits enables you to take deposits on vehicles 24/7
                directly from your website. The on-demand economy continues to
                grow and many customers expect to be able to secure a vehicle
                instantly.
              </p>
              <Link to="/features/online-deposits">
                Learn More <FontAwesomeIcon icon={faAngleRight} />
              </Link>
            </div>
          </div>
          <div className="badges">
            <div className="badge">
              <div className="icon blue">
                <FontAwesomeIcon icon={faFilter} />
              </div>
              <span>Designed to convert</span>
            </div>
            <div className="badge">
              <div className="icon purple">
                <FontAwesomeIcon icon={faWrench} />
              </div>
              <span>Customisable</span>
            </div>
            <div className="badge">
              <img src={PCI} alt="PCI" />
              <span>PCI compliant</span>
            </div>
            <div className="badge">
              <div className="icon dark-blue">
                <FontAwesomeIcon icon={faGlobe} />
              </div>
              <span>Works on all websites</span>
            </div>
          </div>
        </Feature>
        <Feature>
          <div className="text-image">
            <div className="content">
              <h3 className="title">Messenger</h3>
              <h4>Your Online Salesperson</h4>
              <p>
                Your website has far more visitors than that of your showroom,
                however, the website doesn’t have salespeople, until now.
                Messenger is a replacement for conventional live chat, it
                reaches out to visitors and asks if they’d like to leave a
                deposit and generates leads 24/7.
              </p>
              <p>
                Messenger harnesses the power of AI and is pioneering the way
                dealers communicate with customers on their website.
              </p>
              <Link to="/features/messenger">
                Learn More <FontAwesomeIcon icon={faAngleRight} />
              </Link>
            </div>
            <LazyLoadImage
              src={messengerOpen}
              alt="messenger open"
              className="media"
            />
          </div>
        </Feature>
        <Feature>
          <div className="text-image">
            <LazyLoadImage
              src={socialProof}
              alt="Social Proof"
              className="media"
            />
            <div className="content">
              <h3 className="title">Social Proof</h3>
              <h4>Create Urgency</h4>
              <p>
                Show the number of times a vehicle has been viewed recently. Our
                Social Proof prompts make your website visitors aware that they
                should act quickly if they are interested in a vehicle.
              </p>
              <Link to="/features/social-proof">
                Learn More <FontAwesomeIcon icon={faAngleRight} />
              </Link>
            </div>
          </div>
        </Feature>
        {/* <div className="feature">
          <div className="content">
            <h3 className="title">Weather Sensitive Prompt</h3>
            <h4>Take Advantage of The Weather</h4>
            <p>
              Sales of convertibles and four-wheel-drive vehicles are heavily
              influenced by weather conditions. Our weather sensitive prompts
              will promote certain vehicle types depending on the weather
              conditions your website visitors are experiencing.
            </p>
          </div>
          <LazyLoadImage
            src={weatherSensitive}
            alt="Weather Sensitive"
            className="media"
          />
        </div> */}
        <Feature>
          <div className="text-image">
            <div className="content">
              <h3 className="title">Exit-Intent</h3>
              <h4>Send Stock Alerts</h4>
              <p>
                We anticipate website visitors leaving your website and make one
                more effort to convert them. Notifying your subscribers about
                stock alerts is a great way to keep in touch.
              </p>
            </div>
            <LazyLoadImage
              src={exitIntent}
              alt="Exit Intent"
              className="media"
            />
          </div>
        </Feature>
      </div>
    </StyledFeatures>
    <StyledTestimonial>
      <div className="container text-center">
        <Fade>
          <div className="featured-image">
            <LazyLoadImage
              src="https://automerce.imgix.net/9/assets/c4ebe4?w=175"
              alt="Driven Group"
              className="logo"
            />
            <h4 className="light">
              "Using Dealer 9000 is a no brainer, we have seen a 15% increase in
              leads."
            </h4>
            <p>
              <strong>Jeanette Chapman</strong> - Sales Manager, Driven Group
            </p>
          </div>
        </Fade>
      </div>
    </StyledTestimonial>
    <StyledBlog>
      <div className="container">
        <div className="heading">
          <h2 className="light">Dealer Insights</h2>
          <p className="light">Marketing, news and product updates.</p>
        </div>
      </div>
      <div className="carousel-wrapper">
        <div className="carousel">
          {data.allContentfulBlogPost.edges.map(post => (
            <div className="post" key={post.node.slug}>
              <BlogPost
                heroAlt={post.node.heroImage.title}
                heroSrc={post.node.heroImage.resize.src}
                slug={post.node.slug}
                title={post.node.title}
                description={post.node.description.description}
              />
            </div>
          ))}
        </div>
      </div>
    </StyledBlog>
    <ContactBanner />
  </Layout>
)

export default IndexPage
